import { Component, OnInit , DestroyRef, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { TableModule } from 'primeng/table';
import { Button } from 'primeng/button';
import { Dialog } from 'primeng/dialog';
import { ConfirmDialog } from 'primeng/confirmdialog';
import { Select } from 'primeng/select';
import { MultiSelect } from 'primeng/multiselect';
import { InputText } from 'primeng/inputtext';
import { Checkbox } from 'primeng/checkbox';
import { Tooltip } from 'primeng/tooltip';
import { MessageService, ConfirmationService } from 'primeng/api';
import { forkJoin, catchError, of } from 'rxjs';
import { ForecastService } from '../../services/forecast.service';
import { AuthService } from '../../services/auth.service';
import { EmployeeService } from '../../services/employee.service';
import { ProjectService } from '../../services/project.service';
import { DesignationService } from '../../services/designation.service';
import { CategoryService } from '../../services/category.service';
import { ServiceGroupService } from '../../services/service-group.service';
import { YearMonthFilterService } from '../../services/year-month-filter.service';
import { PageHeaderSearchActionService } from '../../services/page-header-search-action.service';
import { Forecast } from '../../models/forecast.model';
import { AllocationStatus } from '../../models/allocation.model';
import { Employee } from '../../models/employee.model';
import { Project } from '../../models/project.model';
import { Designation } from '../../models/designation.model';
import { Category } from '../../models/category.model';
import { ServiceGroup } from '../../models/service-group.model';
import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';

interface DropdownOption {
  label: string;
  value: number | string;
}

@Component({
  selector: 'app-forecast',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    TableModule,
    Button,
    Dialog,
    ConfirmDialog,
    Select,
    MultiSelect,
    InputText,
    Checkbox,
    Tooltip
  ],
  templateUrl: './forecast.component.html',
  styleUrl: './forecast.component.scss'
})
export class ForecastComponent implements OnInit {
  private destroyRef = inject(DestroyRef);
  // Expose AllocationStatus to template
  AllocationStatus = AllocationStatus;

  forecasts: Forecast[] = [];
  filteredForecasts: Forecast[] = [];
  employees: Employee[] = [];
  projects: Project[] = [];
  /** Ids of in-pursuit presales projects — drives the "Presales" chip on forecast cells. */
  presalesProjectIds = new Set<string>();
  designations: Designation[] = [];
  categories: Category[] = [];
  serviceGroups: ServiceGroup[] = [];

  loading: boolean = true;
  displayDialog: boolean = false;
  isEditMode: boolean = false;
  searchText: string = '';

  // Un-Forecasted dialog (eligible employees without a forecast row for the selected year).
  // Parallels the Un-Allocated dialog on the Monthly Allocation page.
  displayUnforecastedDialog: boolean = false;
  loadingUnforecasted: boolean = false;
  unforecastedEmployees: Employee[] = [];

  // Filter properties
  selectedDesignations: string[] = [];
  selectedCategories: string[] = [];
  selectedProjects: string[] = [];
  selectedServiceGroups: string[] = [];

  // Filter options
  designationOptions: DropdownOption[] = [];
  categoryOptions: DropdownOption[] = [];
  projectOptions: DropdownOption[] = [];
  serviceGroupOptions: DropdownOption[] = [];

  // Status options
  statusOptions: DropdownOption[] = [
    { label: 'Planned', value: AllocationStatus.Planned },
    { label: 'Confirmed', value: AllocationStatus.Confirmed }
  ];

  fieldTouched: Record<string, boolean> = {};

  selectedForecast: Forecast = this.getEmptyForecast();

  // Checkbox bindings for status (Planned and Confirmed)
  janPlanned: boolean = false;
  janConfirmed: boolean = false;
  febPlanned: boolean = false;
  febConfirmed: boolean = false;
  marPlanned: boolean = false;
  marConfirmed: boolean = false;
  aprPlanned: boolean = false;
  aprConfirmed: boolean = false;
  mayPlanned: boolean = false;
  mayConfirmed: boolean = false;
  junPlanned: boolean = false;
  junConfirmed: boolean = false;
  julPlanned: boolean = false;
  julConfirmed: boolean = false;
  augPlanned: boolean = false;
  augConfirmed: boolean = false;
  sepPlanned: boolean = false;
  sepConfirmed: boolean = false;
  octPlanned: boolean = false;
  octConfirmed: boolean = false;
  novPlanned: boolean = false;
  novConfirmed: boolean = false;
  decPlanned: boolean = false;
  decConfirmed: boolean = false;

  // Year selection
  selectedYear: number;
  yearOptions: DropdownOption[] = [];

  constructor(
    private forecastService: ForecastService,
    private employeeService: EmployeeService,
    private projectService: ProjectService,
    private designationService: DesignationService,
    private categoryService: CategoryService,
    private serviceGroupService: ServiceGroupService,
    private yearMonthFilterService: YearMonthFilterService,
    private messageService: MessageService,
    private confirmationService: ConfirmationService,
    public pageHeaderService: PageHeaderSearchActionService,
    private authService: AuthService
  ) {
    this.selectedYear = this.yearMonthFilterService.currentYear;
    this.yearOptions = this.yearMonthFilterService.getYearOptions().map((o: { label: string; value: number }) => ({ label: o.label, value: o.value }));
  }

  // Sales is read-only: can view + export, but not add/edit/delete/copy.
  get canWrite(): boolean {
    return this.authService.hasPMAccess();
  }

  get canExport(): boolean {
    return this.authService.hasPMAccess() || this.authService.hasFinanceAccess() || this.authService.getUser()?.isSales === true;
  }

  ngOnInit() {
    this.pageHeaderService.setCanShowFilterToggle(true);
    this.loadLookups();
    this.yearMonthFilterService.year.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(year => {
      this.selectedYear = year;
      this.loadForecasts();
    });
  }

  private loadLookups() {
    forkJoin({
      employees: this.employeeService.getAllEmployees().pipe(
        catchError(error => {
          console.error('Error loading employees:', error);
          return of([] as Employee[]);
        })
      ),
      projects: this.projectService.getAllProjects(true).pipe(
        catchError(error => {
          console.error('Error loading projects:', error);
          return of([] as Project[]);
        })
      ),
      designations: this.designationService.getAllDesignations().pipe(
        catchError(error => {
          console.error('Error loading designations:', error);
          return of([] as Designation[]);
        })
      ),
      categories: this.categoryService.getAllCategories().pipe(
        catchError(error => {
          console.error('Error loading categories:', error);
          return of([] as Category[]);
        })
      ),
      serviceGroups: this.serviceGroupService.getAllServiceGroups().pipe(
        catchError(error => {
          console.error('Error loading service groups:', error);
          return of([] as ServiceGroup[]);
        })
      )
    }).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: (data) => {
        this.employees = (data.employees || []).filter(e => e.isActive && !e.excludeFromForecast);
        // includePresales: true — a pursuit can be forecast against from Lead, same as the
        // allocation grids. Badged so a forecast entry is clearly a pursuit, not committed work.
        this.projects = (data.projects || [])
          .filter(p => p.isActive)
          .map(p => ({ ...p, name: p.isPresales ? `${p.name} (Presales)` : p.name }));
        this.presalesProjectIds = new Set(
          (data.projects || []).filter(p => p.isPresales).map(p => p.id)
        );
        this.designations = (data.designations || []).filter(d => d.isActive);
        this.categories = (data.categories || []).filter(c => c.isActive);
        this.serviceGroups = (data.serviceGroups || []).filter(sg => sg.isActive);
        this.buildFilterOptions();
      },
      error: (error) => {
        console.error('Error loading lookups:', error);
        this.messageService.add({
          severity: 'error',
          summary: 'Error',
          detail: 'Failed to load reference data. Please refresh the page or try again later.'
        });
      }
    });
  }

  private loadForecasts() {
    this.loading = true;
    this.forecastService.getForecastsByYear(this.selectedYear)
      .pipe(
        catchError(error => {
          console.error('Error loading forecasts:', error);
          return of([] as Forecast[]);
        }),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe({
        next: (forecasts) => {
          this.forecasts = forecasts || [];
          this.buildFilterOptions();
          this.applyFilters();
          this.loading = false;
        },
        error: (error) => {
          console.error('Error loading forecasts:', error);
          this.messageService.add({
            severity: 'error',
            summary: 'Error',
            detail: 'Failed to load forecast data. Please refresh the page or try again later.'
          });
          this.loading = false;
        }
      });
  }

  /** Reload everything (lookups + forecasts). Called after copy-to-next-year. */
  loadData() {
    this.loadLookups();
    this.loadForecasts();
  }

  buildFilterOptions() {
    // Build Designation options
    this.designationOptions = this.designations.map(d => ({
      label: d.name,
      value: d.id
    }));

    // Build Category options
    this.categoryOptions = this.categories.map(c => ({
      label: c.name,
      value: c.id
    }));

    // Build Project options
    const projectMap = new Map<string, string>();
    this.forecasts.forEach(f => {
      if (f.janProjectId && f.janProjectName) projectMap.set(f.janProjectId, f.janProjectName);
      if (f.febProjectId && f.febProjectName) projectMap.set(f.febProjectId, f.febProjectName);
      if (f.marProjectId && f.marProjectName) projectMap.set(f.marProjectId, f.marProjectName);
      if (f.aprProjectId && f.aprProjectName) projectMap.set(f.aprProjectId, f.aprProjectName);
      if (f.mayProjectId && f.mayProjectName) projectMap.set(f.mayProjectId, f.mayProjectName);
      if (f.junProjectId && f.junProjectName) projectMap.set(f.junProjectId, f.junProjectName);
      if (f.julProjectId && f.julProjectName) projectMap.set(f.julProjectId, f.julProjectName);
      if (f.augProjectId && f.augProjectName) projectMap.set(f.augProjectId, f.augProjectName);
      if (f.sepProjectId && f.sepProjectName) projectMap.set(f.sepProjectId, f.sepProjectName);
      if (f.octProjectId && f.octProjectName) projectMap.set(f.octProjectId, f.octProjectName);
      if (f.novProjectId && f.novProjectName) projectMap.set(f.novProjectId, f.novProjectName);
      if (f.decProjectId && f.decProjectName) projectMap.set(f.decProjectId, f.decProjectName);
    });
    this.projects.forEach(p => {
      if (p.id && p.name) {
        projectMap.set(p.id, p.name);
      }
    });

    this.projectOptions = Array.from(projectMap.entries())
      .map(([id, name]) => ({ label: name || 'Unknown', value: id }))
      .sort((a, b) => a.label.localeCompare(b.label));

    // Build Service Group options - get from forecasts and all available service groups
    const serviceGroupMap = new Map<string, string>();

    // First, add service groups from forecasts
    this.forecasts.forEach(f => {
      if (f.serviceGroupId && f.serviceGroupName) {
        serviceGroupMap.set(f.serviceGroupId, f.serviceGroupName);
      }
    });

    // Then, add all service groups from the database
    this.serviceGroups.forEach(sg => {
      if (sg.id && sg.name) {
        serviceGroupMap.set(sg.id, sg.name);
      }
    });

    this.serviceGroupOptions = Array.from(serviceGroupMap.entries())
      .map(([id, name]) => ({ label: name || 'Unknown', value: id }))
      .sort((a, b) => a.label.localeCompare(b.label));
  }

  applyFilters() {
    // Only show forecasts for the selected year (in case API returns multiple years)
    this.filteredForecasts = this.forecasts
      .filter(forecast => forecast.year === this.selectedYear)
      .filter(forecast => {
      // Apply search text filter
      if (this.searchText) {
        const searchLower = this.searchText.toLowerCase();
        const matchesSearch =
          forecast.employeeName?.toLowerCase().includes(searchLower) ||
          forecast.designationName?.toLowerCase().includes(searchLower) ||
          forecast.categoryName?.toLowerCase().includes(searchLower) ||
          forecast.janProjectName?.toLowerCase().includes(searchLower) ||
          forecast.febProjectName?.toLowerCase().includes(searchLower) ||
          forecast.marProjectName?.toLowerCase().includes(searchLower) ||
          forecast.aprProjectName?.toLowerCase().includes(searchLower) ||
          forecast.mayProjectName?.toLowerCase().includes(searchLower) ||
          forecast.junProjectName?.toLowerCase().includes(searchLower) ||
          forecast.julProjectName?.toLowerCase().includes(searchLower) ||
          forecast.augProjectName?.toLowerCase().includes(searchLower) ||
          forecast.sepProjectName?.toLowerCase().includes(searchLower) ||
          forecast.octProjectName?.toLowerCase().includes(searchLower) ||
          forecast.novProjectName?.toLowerCase().includes(searchLower) ||
          forecast.decProjectName?.toLowerCase().includes(searchLower);
        if (!matchesSearch) return false;
      }

      // Apply designation filter
      if (this.selectedDesignations.length > 0 && forecast.designationId && !this.selectedDesignations.includes(forecast.designationId)) {
        return false;
      }

      // Apply category filter
      if (this.selectedCategories.length > 0 && forecast.categoryId && !this.selectedCategories.includes(forecast.categoryId)) {
        return false;
      }

      // Apply project filter
      if (this.selectedProjects.length > 0) {
        const anyProjectMatch =
          (forecast.janProjectId && this.selectedProjects.includes(forecast.janProjectId)) ||
          (forecast.febProjectId && this.selectedProjects.includes(forecast.febProjectId)) ||
          (forecast.marProjectId && this.selectedProjects.includes(forecast.marProjectId)) ||
          (forecast.aprProjectId && this.selectedProjects.includes(forecast.aprProjectId)) ||
          (forecast.mayProjectId && this.selectedProjects.includes(forecast.mayProjectId)) ||
          (forecast.junProjectId && this.selectedProjects.includes(forecast.junProjectId)) ||
          (forecast.julProjectId && this.selectedProjects.includes(forecast.julProjectId)) ||
          (forecast.augProjectId && this.selectedProjects.includes(forecast.augProjectId)) ||
          (forecast.sepProjectId && this.selectedProjects.includes(forecast.sepProjectId)) ||
          (forecast.octProjectId && this.selectedProjects.includes(forecast.octProjectId)) ||
          (forecast.novProjectId && this.selectedProjects.includes(forecast.novProjectId)) ||
          (forecast.decProjectId && this.selectedProjects.includes(forecast.decProjectId));
        if (!anyProjectMatch) return false;
      }

      // Apply service group filter
      if (this.selectedServiceGroups.length > 0 && forecast.serviceGroupId && !this.selectedServiceGroups.includes(forecast.serviceGroupId)) {
        return false;
      }

      return true;
    });
  }

  /** True if any filter or search is applied (for showing clear icon). */
  hasActiveFilters(): boolean {
    return (
      (this.searchText ?? '').trim().length > 0 ||
      (this.selectedDesignations?.length ?? 0) > 0 ||
      (this.selectedCategories?.length ?? 0) > 0 ||
      (this.selectedProjects?.length ?? 0) > 0 ||
      (this.selectedServiceGroups?.length ?? 0) > 0
    );
  }

  clearFilters() {
    this.selectedDesignations = [];
    this.selectedCategories = [];
    this.selectedProjects = [];
    this.selectedServiceGroups = [];
    this.searchText = '';
    this.applyFilters();
  }

  onYearChange() {
    this.loadForecasts();
  }

  onSearch() {
    this.applyFilters();
  }

  clearSearch() {
    this.searchText = '';
    this.applyFilters();
  }

  showAddDialog() {
    this.selectedForecast = this.getEmptyForecast();
    this.selectedForecast.year = this.selectedYear;
    this.isEditMode = false;
    this.resetCheckboxes();
    this.fieldTouched = {};
    this.displayDialog = true;
  }

  showEditDialog(forecast: Forecast) {
    this.selectedForecast = { ...forecast };
    this.isEditMode = true;
    this.updateCheckboxes();
    this.fieldTouched = {};
    this.displayDialog = true;
  }

  resetCheckboxes() {
    this.janPlanned = false;
    this.janConfirmed = false;
    this.febPlanned = false;
    this.febConfirmed = false;
    this.marPlanned = false;
    this.marConfirmed = false;
    this.aprPlanned = false;
    this.aprConfirmed = false;
    this.mayPlanned = false;
    this.mayConfirmed = false;
    this.junPlanned = false;
    this.junConfirmed = false;
    this.julPlanned = false;
    this.julConfirmed = false;
    this.augPlanned = false;
    this.augConfirmed = false;
    this.sepPlanned = false;
    this.sepConfirmed = false;
    this.octPlanned = false;
    this.octConfirmed = false;
    this.novPlanned = false;
    this.novConfirmed = false;
    this.decPlanned = false;
    this.decConfirmed = false;
  }

  updateCheckboxes() {
    this.janPlanned = this.selectedForecast.janStatus === AllocationStatus.Planned;
    this.janConfirmed = this.selectedForecast.janStatus === AllocationStatus.Confirmed;
    this.febPlanned = this.selectedForecast.febStatus === AllocationStatus.Planned;
    this.febConfirmed = this.selectedForecast.febStatus === AllocationStatus.Confirmed;
    this.marPlanned = this.selectedForecast.marStatus === AllocationStatus.Planned;
    this.marConfirmed = this.selectedForecast.marStatus === AllocationStatus.Confirmed;
    this.aprPlanned = this.selectedForecast.aprStatus === AllocationStatus.Planned;
    this.aprConfirmed = this.selectedForecast.aprStatus === AllocationStatus.Confirmed;
    this.mayPlanned = this.selectedForecast.mayStatus === AllocationStatus.Planned;
    this.mayConfirmed = this.selectedForecast.mayStatus === AllocationStatus.Confirmed;
    this.junPlanned = this.selectedForecast.junStatus === AllocationStatus.Planned;
    this.junConfirmed = this.selectedForecast.junStatus === AllocationStatus.Confirmed;
    this.julPlanned = this.selectedForecast.julStatus === AllocationStatus.Planned;
    this.julConfirmed = this.selectedForecast.julStatus === AllocationStatus.Confirmed;
    this.augPlanned = this.selectedForecast.augStatus === AllocationStatus.Planned;
    this.augConfirmed = this.selectedForecast.augStatus === AllocationStatus.Confirmed;
    this.sepPlanned = this.selectedForecast.sepStatus === AllocationStatus.Planned;
    this.sepConfirmed = this.selectedForecast.sepStatus === AllocationStatus.Confirmed;
    this.octPlanned = this.selectedForecast.octStatus === AllocationStatus.Planned;
    this.octConfirmed = this.selectedForecast.octStatus === AllocationStatus.Confirmed;
    this.novPlanned = this.selectedForecast.novStatus === AllocationStatus.Planned;
    this.novConfirmed = this.selectedForecast.novStatus === AllocationStatus.Confirmed;
    this.decPlanned = this.selectedForecast.decStatus === AllocationStatus.Planned;
    this.decConfirmed = this.selectedForecast.decStatus === AllocationStatus.Confirmed;
  }

  // Status change handlers - only one can be selected at a time (radio button behavior)
  onJanStatusChange(checked: boolean, type: 'planned' | 'confirmed') {
    if (checked) {
      if (type === 'planned') {
        this.janConfirmed = false;
        this.selectedForecast.janStatus = AllocationStatus.Planned;
      } else {
        this.janPlanned = false;
        this.selectedForecast.janStatus = AllocationStatus.Confirmed;
      }
    } else {
      this.selectedForecast.janStatus = undefined;
    }
  }

  onFebStatusChange(checked: boolean, type: 'planned' | 'confirmed') {
    if (checked) {
      if (type === 'planned') {
        this.febConfirmed = false;
        this.selectedForecast.febStatus = AllocationStatus.Planned;
      } else {
        this.febPlanned = false;
        this.selectedForecast.febStatus = AllocationStatus.Confirmed;
      }
    } else {
      this.selectedForecast.febStatus = undefined;
    }
  }

  onMarStatusChange(checked: boolean, type: 'planned' | 'confirmed') {
    if (checked) {
      if (type === 'planned') {
        this.marConfirmed = false;
        this.selectedForecast.marStatus = AllocationStatus.Planned;
      } else {
        this.marPlanned = false;
        this.selectedForecast.marStatus = AllocationStatus.Confirmed;
      }
    } else {
      this.selectedForecast.marStatus = undefined;
    }
  }

  onAprStatusChange(checked: boolean, type: 'planned' | 'confirmed') {
    if (checked) {
      if (type === 'planned') {
        this.aprConfirmed = false;
        this.selectedForecast.aprStatus = AllocationStatus.Planned;
      } else {
        this.aprPlanned = false;
        this.selectedForecast.aprStatus = AllocationStatus.Confirmed;
      }
    } else {
      this.selectedForecast.aprStatus = undefined;
    }
  }

  onMayStatusChange(checked: boolean, type: 'planned' | 'confirmed') {
    if (checked) {
      if (type === 'planned') {
        this.mayConfirmed = false;
        this.selectedForecast.mayStatus = AllocationStatus.Planned;
      } else {
        this.mayPlanned = false;
        this.selectedForecast.mayStatus = AllocationStatus.Confirmed;
      }
    } else {
      this.selectedForecast.mayStatus = undefined;
    }
  }

  onJunStatusChange(checked: boolean, type: 'planned' | 'confirmed') {
    if (checked) {
      if (type === 'planned') {
        this.junConfirmed = false;
        this.selectedForecast.junStatus = AllocationStatus.Planned;
      } else {
        this.junPlanned = false;
        this.selectedForecast.junStatus = AllocationStatus.Confirmed;
      }
    } else {
      this.selectedForecast.junStatus = undefined;
    }
  }

  onJulStatusChange(checked: boolean, type: 'planned' | 'confirmed') {
    if (checked) {
      if (type === 'planned') {
        this.julConfirmed = false;
        this.selectedForecast.julStatus = AllocationStatus.Planned;
      } else {
        this.julPlanned = false;
        this.selectedForecast.julStatus = AllocationStatus.Confirmed;
      }
    } else {
      this.selectedForecast.julStatus = undefined;
    }
  }

  onAugStatusChange(checked: boolean, type: 'planned' | 'confirmed') {
    if (checked) {
      if (type === 'planned') {
        this.augConfirmed = false;
        this.selectedForecast.augStatus = AllocationStatus.Planned;
      } else {
        this.augPlanned = false;
        this.selectedForecast.augStatus = AllocationStatus.Confirmed;
      }
    } else {
      this.selectedForecast.augStatus = undefined;
    }
  }

  onSepStatusChange(checked: boolean, type: 'planned' | 'confirmed') {
    if (checked) {
      if (type === 'planned') {
        this.sepConfirmed = false;
        this.selectedForecast.sepStatus = AllocationStatus.Planned;
      } else {
        this.sepPlanned = false;
        this.selectedForecast.sepStatus = AllocationStatus.Confirmed;
      }
    } else {
      this.selectedForecast.sepStatus = undefined;
    }
  }

  onOctStatusChange(checked: boolean, type: 'planned' | 'confirmed') {
    if (checked) {
      if (type === 'planned') {
        this.octConfirmed = false;
        this.selectedForecast.octStatus = AllocationStatus.Planned;
      } else {
        this.octPlanned = false;
        this.selectedForecast.octStatus = AllocationStatus.Confirmed;
      }
    } else {
      this.selectedForecast.octStatus = undefined;
    }
  }

  onNovStatusChange(checked: boolean, type: 'planned' | 'confirmed') {
    if (checked) {
      if (type === 'planned') {
        this.novConfirmed = false;
        this.selectedForecast.novStatus = AllocationStatus.Planned;
      } else {
        this.novPlanned = false;
        this.selectedForecast.novStatus = AllocationStatus.Confirmed;
      }
    } else {
      this.selectedForecast.novStatus = undefined;
    }
  }

  onDecStatusChange(checked: boolean, type: 'planned' | 'confirmed') {
    if (checked) {
      if (type === 'planned') {
        this.decConfirmed = false;
        this.selectedForecast.decStatus = AllocationStatus.Planned;
      } else {
        this.decPlanned = false;
        this.selectedForecast.decStatus = AllocationStatus.Confirmed;
      }
    } else {
      this.selectedForecast.decStatus = undefined;
    }
  }

  hideDialog() {
    this.displayDialog = false;
    this.selectedForecast = this.getEmptyForecast();
    this.fieldTouched = {};
  }

  /** Reset form to empty state (keeps dialog open). */
  resetForecastForm() {
    this.selectedForecast = this.getEmptyForecast();
    this.selectedForecast.year = this.selectedYear;
    this.resetCheckboxes();
    this.fieldTouched = {};
  }

  isForecastFormValid(): boolean {
    return !!this.selectedForecast.employeeId;
  }

  saveForecast() {
    // Mark all required fields as touched
    this.fieldTouched['employeeId'] = true;

    if (!this.isForecastFormValid()) {
      return;
    }

    if (this.isEditMode) {
      this.forecastService.updateForecast(this.selectedForecast.id, this.selectedForecast).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
        next: (updatedForecast) => {
          this.messageService.add({
            severity: 'success',
            summary: 'Success',
            detail: 'Forecast updated successfully'
          });
          // Optimistic update: replace forecast in local array by ID
          const index = this.forecasts.findIndex(f => f.id === updatedForecast.id);
          if (index !== -1) {
            this.forecasts[index] = updatedForecast;
          }
          this.buildFilterOptions();
          this.applyFilters();
          this.hideDialog();
        },
        error: (error) => {
          console.error('Error updating forecast:', error);
          this.messageService.add({
            severity: 'error',
            summary: 'Error',
            detail: 'Failed to update forecast'
          });
        }
      });
    } else {
      this.forecastService.addForecast(this.selectedForecast).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
        next: (newForecast) => {
          this.messageService.add({
            severity: 'success',
            summary: 'Success',
            detail: 'Forecast added successfully'
          });
          // Optimistic update: push new forecast to local array
          this.forecasts.push(newForecast);
          this.buildFilterOptions();
          this.applyFilters();
          this.hideDialog();
        },
        error: (error) => {
          console.error('Error adding forecast:', error);
          this.messageService.add({
            severity: 'error',
            summary: 'Error',
            detail: 'Failed to add forecast'
          });
        }
      });
    }
  }

  deleteForecast(forecast: Forecast) {
    this.confirmationService.confirm({
      message: `Are you sure you want to delete this forecast row for "${forecast.employeeName}" (year ${forecast.year})? This action cannot be undone.`,
      header: 'Confirm Delete',
      icon: 'pi pi-exclamation-triangle',
      acceptButtonStyleClass: 'p-button-danger',
      accept: () => {
        this.forecastService.deleteForecast(forecast.id).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
          next: () => {
            this.messageService.add({
              severity: 'success',
              summary: 'Success',
              detail: 'Forecast deleted successfully'
            });
            // Optimistic update: filter out deleted forecast from local array
            this.forecasts = this.forecasts.filter(f => f.id !== forecast.id);
            this.buildFilterOptions();
            this.applyFilters();
          },
          error: (error) => {
            console.error('Error deleting forecast:', error);
            this.messageService.add({
              severity: 'error',
              summary: 'Error',
              detail: 'Failed to delete forecast'
            });
          }
        });
      }
    });
  }

  copyToNextYear() {
    const sourceYear = this.selectedYear;
    const targetYear = sourceYear + 1;

    this.confirmationService.confirm({
      message: `Copy employee forecast list from ${sourceYear} to ${targetYear}? Only missing employees will be inserted as blank entries.`,
      header: 'Confirm Copy',
      icon: 'pi pi-exclamation-triangle',
      accept: () => {
        this.loading = true;
        this.forecastService.copyToNextYear(sourceYear).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
          next: (result) => {
            this.messageService.add({
              severity: 'success',
              summary: 'Copy completed',
              detail: `Inserted ${result.insertedCount} of ${result.sourceCount} employees into ${result.targetYear} (skipped ${result.skippedCount}).`
            });
            this.loading = false;
            this.loadData();
          },
          error: (error) => {
            console.error('Error copying forecasts to next year:', error);
            this.messageService.add({
              severity: 'error',
              summary: 'Error',
              detail: 'Failed to copy forecast employee list to next year'
            });
            this.loading = false;
          }
        });
      }
    });
  }

  onEmployeeChange(employeeId: string) {
    if (employeeId) {
      const selectedEmployee = this.employees.find(e => e.id === employeeId);
      if (selectedEmployee) {
        // Auto-populate designation and category from employee
        this.selectedForecast.designationId = selectedEmployee.designationId;
        this.selectedForecast.categoryId = selectedEmployee.categoryId;
      }
    }
  }

  get activeEmployees(): Employee[] {
    return this.employees.filter(e => e.isActive && !e.excludeFromForecast);
  }

  get employeeOptionsForDialog(): Employee[] {
    // In edit mode, keep current employee selectable.
    if (this.isEditMode) {
      return this.activeEmployees;
    }

    // In add mode, hide employees already in current year's forecast grid.
    const existingEmployeeIds = new Set(this.forecasts.map(f => f.employeeId));
    return this.activeEmployees.filter(e => !existingEmployeeIds.has(e.id));
  }

  get activeProjects(): Project[] {
    return this.projects.filter(p => p.isActive);
  }

  /**
   * Check if a project name represents "Available" status
   */
  isAvailable(projectName: string | undefined | null): boolean {
    if (!projectName) return false;
    return projectName.trim().toLowerCase() === 'available';
  }

  /**
   * True when a forecast month points at an in-pursuit presales deal, so the cell can show a
   * "Presales" chip. Keyed on projectId rather than name: this.projects already carries the
   * "(Presales)" suffix on `name` for the dropdown, and the stored forecast name does not.
   * Built once into a Set so 12 cells x N employees stay cheap.
   */
  isPresalesProject(projectId: string | undefined | null): boolean {
    if (!projectId) return false;
    return this.presalesProjectIds.has(projectId);
  }

  /** Initials for employee avatar (e.g. "John Doe" -> "JD"). */
  getEmployeeInitials(employeeName: string | undefined | null): string {
    if (!employeeName || !employeeName.trim()) return '—';
    const parts = employeeName.trim().split(/\s+/);
    if (parts.length >= 2) {
      return (parts[0].charAt(0) + parts[parts.length - 1].charAt(0)).toUpperCase();
    }
    return parts[0].slice(0, 2).toUpperCase();
  }

  /** Designation and category for employee column sublabel. */
  getEmployeeMeta(forecast: Forecast): string {
    const des = forecast.designationName?.trim() || '';
    const cat = forecast.categoryName?.trim() || '';
    if (des && cat) return `${des} · ${cat}`;
    return des || cat || '—';
  }

  /** Tooltip for employee cell. */
  getEmployeeTooltip(forecast: Forecast): string {
    const parts = [forecast.employeeName, forecast.designationName, forecast.categoryName].filter(Boolean);
    return parts.join(' · ') || '—';
  }

  exportToExcel() {
    this.forecastService.exportToExcel(this.selectedYear).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: (blob: Blob) => {
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `Team_Allocation_Forecast_${this.selectedYear}.xlsx`;
        link.click();
        window.URL.revokeObjectURL(url);

        this.messageService.add({
          severity: 'success',
          summary: 'Success',
          detail: 'Forecast Data Exported to Excel successfully'
        });
      },
      error: (error) => {
        console.error('Error exporting to Excel:', error);
        this.messageService.add({
          severity: 'error',
          summary: 'Error',
          detail: 'Failed to export forecast data to Excel'
        });
      }
    });
  }

  private formatDate(date: Date): string {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}${month}${day}`;
  }

  private getEmptyForecast(): Forecast {
    return {
      id: '',
      employeeId: '',
      designationId: undefined,
      categoryId: undefined,
      year: this.selectedYear,
      janProjectId: undefined,
      febProjectId: undefined,
      marProjectId: undefined,
      aprProjectId: undefined,
      mayProjectId: undefined,
      junProjectId: undefined,
      julProjectId: undefined,
      augProjectId: undefined,
      sepProjectId: undefined,
      octProjectId: undefined,
      novProjectId: undefined,
      decProjectId: undefined
    };
  }

  getStatusName(status: AllocationStatus | undefined): string {
    if (!status) return '';
    return status === AllocationStatus.Confirmed ? 'Confirmed' : 'Planned';
  }

  // ===== Stats Card Getters =====
  private readonly _monthStatusKeys: Array<keyof Forecast> = [
    'janStatus','febStatus','marStatus','aprStatus','mayStatus','junStatus',
    'julStatus','augStatus','sepStatus','octStatus','novStatus','decStatus'
  ];
  private readonly _monthProjectKeys: Array<keyof Forecast> = [
    'janProjectId','febProjectId','marProjectId','aprProjectId','mayProjectId','junProjectId',
    'julProjectId','augProjectId','sepProjectId','octProjectId','novProjectId','decProjectId'
  ];

  get forecastTotalCount(): number {
    return this.filteredForecasts.length;
  }

  get forecastConfirmedCount(): number {
    return this.filteredForecasts.filter(f =>
      this._monthStatusKeys.some(k => f[k] === AllocationStatus.Confirmed)
    ).length;
  }

  get forecastUniqueProjectCount(): number {
    const ids = new Set<string>();
    this.filteredForecasts.forEach(f =>
      this._monthProjectKeys.forEach(k => { if (f[k]) ids.add(f[k] as string); })
    );
    return ids.size;
  }

  get forecastConfirmedPercent(): number {
    return this.forecastTotalCount === 0 ? 0
      : Math.round((this.forecastConfirmedCount / this.forecastTotalCount) * 100);
  }

  /**
   * Count of employees who are eligible for forecast (active, not excluded) but have NO
   * Forecast row for the currently selected year. This is the gap that explains why the
   * Forecast "Total Employees" stat can differ from the Monthly Allocation "Total Employees"
   * stat — the latter counts every eligible employee, while this grid only shows employees
   * that have been explicitly added to the forecast.
   */
  get unforecastedCount(): number {
    return this.getUnforecastedEmployees().length;
  }

  private getUnforecastedEmployees(): Employee[] {
    const inForecast = new Set(
      this.forecasts
        .filter(f => f.year === this.selectedYear)
        .map(f => f.employeeId)
    );
    // `this.employees` is already filtered to active && !excludeFromForecast in loadData()
    return this.employees.filter(e => !inForecast.has(e.id));
  }

  showUnforecastedDialog(): void {
    this.displayUnforecastedDialog = true;
    this.loadingUnforecasted = true;
    try {
      this.unforecastedEmployees = this.getUnforecastedEmployees();
    } catch (error) {
      console.error('Error calculating un-forecasted employees:', error);
      this.messageService.add({
        severity: 'error',
        summary: 'Error',
        detail: 'Failed to calculate un-forecasted employees'
      });
      this.unforecastedEmployees = [];
    } finally {
      this.loadingUnforecasted = false;
    }
  }

  hideUnforecastedDialog(): void {
    this.displayUnforecastedDialog = false;
  }

  /** Close the Un-Forecasted dialog and open the Add Forecast dialog with the given employee pre-selected. */
  openAddForecastForEmployee(employee: Employee): void {
    this.hideUnforecastedDialog();
    setTimeout(() => {
      this.selectedForecast = this.getEmptyForecast();
      this.selectedForecast.year = this.selectedYear;
      this.selectedForecast.employeeId = employee.id;
      this.onEmployeeChange(employee.id);
      this.isEditMode = false;
      this.resetCheckboxes();
      this.fieldTouched = {};
      this.displayDialog = true;
    }, 150);
  }


  exportUnforecastedToExcel(): void {
    if (!this.unforecastedEmployees || this.unforecastedEmployees.length === 0) {
      this.messageService.add({
        severity: 'warn',
        summary: 'No Data',
        detail: 'No un-forecasted employees to export'
      });
      return;
    }

    try {
      const worksheetData = this.unforecastedEmployees.map(emp => ({
        'Employee Name': emp.name || '',
        'Email': emp.email || '',
        'Designation': emp.designationName || '',
        'Category': emp.categoryName || '',
        'Service Group': emp.serviceGroupName || ''
      }));

      const worksheet: XLSX.WorkSheet = XLSX.utils.json_to_sheet(worksheetData);
      worksheet['!cols'] = [{ wch: 28 }, { wch: 28 }, { wch: 20 }, { wch: 20 }, { wch: 22 }];
      const workbook: XLSX.WorkBook = {
        Sheets: { 'Un-Forecasted': worksheet },
        SheetNames: ['Un-Forecasted']
      };
      const buffer: unknown = XLSX.write(workbook, { bookType: 'xlsx', type: 'array' });
      const blob = new Blob([buffer as BlobPart], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
      });
      saveAs(blob, `Forecast_Unforecasted_${this.selectedYear}.xlsx`);

      this.messageService.add({
        severity: 'success',
        summary: 'Success',
        detail: 'Un-forecasted employees exported to Excel successfully'
      });
    } catch (error) {
      console.error('Error exporting un-forecasted employees:', error);
      this.messageService.add({
        severity: 'error',
        summary: 'Error',
        detail: 'Failed to export un-forecasted employees to Excel'
      });
    }
  }
}
